/**
 * /goals — Goals tree view.
 *
 * Top-level shows the rolled-up tree of objectives (using GoalsTree).
 * A status filter narrows the rendered set without re-querying (the
 * tree is small enough that client-side filtering is fine).
 */
import type { Goal } from '@chaos/types/constitution';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, Plus, Target } from 'lucide-react';
import * as React from 'react';

import { GoalsTree } from '../../../components/constitution/goals-tree';
import { Button } from '../../../components/ui/button';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const STATUS_FILTERS = ['all', 'open', 'in_progress', 'blocked', 'done', 'dropped'] as const;

function GoalsRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canEdit = role === 'founder' || role === 'admin';

  const [statusFilter, setStatusFilter] = React.useState<(typeof STATUS_FILTERS)[number]>('all');

  // Pull everything in a single request — the tree builds locally.
  const goalsQ = useQuery({
    queryKey: constitutionKeys.goals({}),
    queryFn: () => constitutionApi.listGoals({}),
    staleTime: 30_000,
  });
  const goals: Goal[] = goalsQ.data?.goals ?? [];

  const filtered = React.useMemo(() => {
    if (statusFilter === 'all') return goals;
    return goals.filter((g) => g.status === statusFilter);
  }, [goals, statusFilter]);

  const counts = React.useMemo(() => {
    const c: Record<Goal['status'], number> = {
      open: 0,
      in_progress: 0,
      blocked: 0,
      done: 0,
      dropped: 0,
    };
    for (const g of goals) c[g.status] += 1;
    return c;
  }, [goals]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Target className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Goals</h1>
          <p className="text-[12px] text-muted-foreground">
            Tree of organisational outcomes — parent/child rollup, status, owner.
          </p>
        </div>
        <FilterChips value={statusFilter} onChange={setStatusFilter} counts={counts} />
        {canEdit ? (
          <Button variant="outline" size="sm" disabled className="ml-auto h-7" aria-label="Add">
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add goal
          </Button>
        ) : null}
      </header>

      <div className="rounded border border-border bg-background p-3">
        {goalsQ.isLoading ? (
          <p className="flex items-center justify-center gap-2 py-6 text-[12px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            Loading goals…
          </p>
        ) : (
          <GoalsTree goals={filtered} />
        )}
      </div>
    </section>
  );
}

interface FilterChipsProps {
  value: (typeof STATUS_FILTERS)[number];
  onChange: (v: (typeof STATUS_FILTERS)[number]) => void;
  counts: Record<Goal['status'], number>;
}

function FilterChips({ value, onChange, counts }: FilterChipsProps) {
  return (
    <div className="ml-3 flex items-center gap-0.5 rounded border border-border bg-background p-0.5 text-[11px]">
      {STATUS_FILTERS.map((opt) => {
        const count =
          opt === 'all'
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[opt as Goal['status']] ?? 0);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={value === opt}
            onClick={() => onChange(opt)}
            className={cn(
              'rounded px-2 py-0.5 capitalize transition-colors',
              value === opt
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
            )}
          >
            {opt.replace('_', ' ')}{' '}
            <span className="font-mono tabular-nums text-muted-foreground/70">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/goals/')({
  component: GoalsRoute,
});
